// src/components/settings/ContributionBasisCard.js
//
// PRD_PhilHealthContributionBasis H9 — one card, rendered once per agency.
//
// Extracted verbatim from the SSS card shipped by PRD_SssContributionBasis so
// the two agencies cannot drift apart. Every locked string it carries is that
// card's, with the agency name substituted; the differences are listed in the
// PRD's Client strings table and are the `floorDisclosure` and `agencyKey`
// props below.
//
// The lock banner and the mid-month warning do NOT live here — they name no
// agency, so H9 renders them once for the whole section rather than twice.

import React from "react";
import Icon from "../icons/Icon";
import { SSS_BASIS_FACTORS } from "../../constants/sssBasisFactors";

const ContributionBasisCard = ({
  // Display name: "SSS" | "PhilHealth". Used in every visible and accessible
  // string on the card.
  agency,
  // Stable key for form wiring: "sss" | "philhealth". The radio group's `name`
  // MUST differ between the two cards — an identical name makes the browser
  // treat both cards' radios as ONE group, so choosing a valuation on one card
  // silently clears the other's.
  agencyKey,
  basis,
  canEdit,
  locked,
  onToggleFactor,
  onPieceRateValuation,
  // SSS discloses its ₱5,000 floor (C9). PhilHealth deliberately shows no
  // figure: its floor is a published table parameter, not a constant, so a
  // hard-coded peso amount here would go stale the next time PhilHealth
  // revises — and the card states no agency rule by design.
  floorDisclosure,
}) => {
  const controlsDisabled = !canEdit || Boolean(locked) || !basis?.enabled;
  const valuation = basis?.pieceRateValuation === "daily" ? "daily" : "piece";

  return (
    // A labelled landmark, not a bare div: with two of these cards on one page
    // every string inside them appears twice, so both assistive tech and the
    // tests need a way to say WHICH card. `within(getByRole("region", …))` is
    // the sanctioned scoping the regression proof allows.
    <section className="settings-card" aria-label={`${agency} Contribution Basis`}>
      <div className="settings-card-header">
        <div className="settings-card-header-left">
          <div className="settings-card-icon gold">
            <span role="img" aria-label="government">
              <Icon name="landmark" size={20} />
            </span>
          </div>
          <div>
            <h2 className="settings-card-title">{agency} Contribution Basis</h2>
            <p className="settings-card-description">
              Compose the {agency} basis from actual pay in the previous month
            </p>
          </div>
        </div>
      </div>

      <div className="settings-card-body">
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: !canEdit || locked ? "default" : "pointer",
            opacity: !canEdit || locked ? 0.6 : 1,
          }}
        >
          <input
            type="checkbox"
            aria-label={`Enable ${agency} contribution basis`}
            checked={Boolean(basis?.enabled)}
            disabled={!canEdit || Boolean(locked)}
            onChange={() => onToggleFactor("enabled")}
          />
          <span style={{ fontWeight: 600 }}>
            Enable {agency} contribution basis
          </span>
        </label>

        {/* Basic pay is ALWAYS part of the basis and is deliberately not a
            checkbox (C2, revised at /test-impl): unticking it zeroed every
            monthly-salaried employee and drove a basis negative on real data,
            which is a misfiled contribution rather than a policy choice. */}
        <div className="settings-input-hint" style={{ marginTop: 16 }}>
          Basic pay is always included. The components below adjust it.
        </div>

        {/* The optional factors, in two labelled groups so the sign is
            structural rather than inferred from a label (C3). Disabled — but
            VISIBLE — while the toggle is off, so the operator can see what
            enabling turns on. The aria-label prefix keeps these checkboxes'
            accessible names distinct from the Minimum Wage card's boxes AND
            from the sibling agency's; the VISIBLE label is the C22 string. */}
        {[
          { sign: "add", title: "Added to the basis" },
          { sign: "subtract", title: "Subtracted from the basis" },
        ].map((group) => (
          <div key={group.sign} style={{ marginTop: 16 }}>
            <div className="form-label">{group.title}</div>
            {SSS_BASIS_FACTORS.filter(
              (f) => !f.alwaysOn && f.sign === group.sign
            ).map((f) => (
              <label
                key={f.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 10,
                  cursor: controlsDisabled ? "default" : "pointer",
                  opacity: controlsDisabled ? 0.6 : 1,
                }}
              >
                <input
                  type="checkbox"
                  aria-label={`${agency} basis — ${f.label}`}
                  checked={Boolean(basis?.factors?.[f.key])}
                  disabled={controlsDisabled}
                  onChange={() => onToggleFactor(f.key)}
                />
                <span style={{ fontWeight: 600 }}>{f.label}</span>
              </label>
            ))}
          </div>
        ))}

        {/* C5 — the attendance gate, named so "the setting does nothing" is not
            filed as a bug. Locked string. */}
        <div className="settings-input-hint" style={{ marginTop: 16 }}>
          These factors apply to daily-rate employees. For monthly-salaried
          employees they apply only when Pay Basis is set to Attendance.
        </div>
        {floorDisclosure && (
          <div className="settings-input-hint" style={{ marginTop: 8 }}>
            {floorDisclosure}
          </div>
        )}

        {/* P1 — piece-rate valuation. A two-way choice, NOT a seventh factor
            checkbox: unticking a factor always lowers the basis, whereas
            valuing piece-rate days at the daily rate can RAISE it. Placed after
            both hints so the C5 string keeps referring to the checkbox groups
            it was written for — it is false of this control, which is inert for
            monthly employees under any pay basis. Shown to every company: with
            no piece-rate days the basis is byte-identical either way. */}
        <div style={{ marginTop: 16 }}>
          <div className="form-label">Piece-rate days</div>
          {[
            { value: "piece", label: "Value at the piece amount" },
            { value: "daily", label: "Value at the daily rate" },
          ].map((opt) => (
            <label
              key={opt.value}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 10,
                cursor: controlsDisabled ? "default" : "pointer",
                opacity: controlsDisabled ? 0.6 : 1,
              }}
            >
              <input
                type="radio"
                name={`${agencyKey}BasisPieceRateValuation`}
                // Deliberately NOT agency-prefixed: the shipped SSS test pins
                // this exact accessible name, and the regression proof forbids
                // changing an existing assertion. With two cards on one page
                // the name is ambiguous, which is what `within(card)` in the
                // tests is for — the sanctioned exception.
                aria-label={opt.label}
                value={opt.value}
                checked={valuation === opt.value}
                disabled={controlsDisabled}
                onChange={() => onPieceRateValuation(opt.value)}
              />
              <span style={{ fontWeight: 600 }}>{opt.label}</span>
            </label>
          ))}
          {/* Locked string. Known and deliberately unstated (impl-review r2,
              edge finding): the option is also inert for a daily employee whose
              dailySalary is 0, because P3's re-valuation is a SUBSTITUTION and
              computeEarnings keeps the piece amount rather than composing a ₱0
              basis. Left out — no employee can be SAVED with a zero salary, so
              the only way to meet it is legacy data. */}
          <div className="settings-input-hint" style={{ marginTop: 8 }}>
            Applies to daily-rate employees with piece-rate days. Piece-rate pay
            itself is unchanged — this affects only the {agency} basis.
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContributionBasisCard;
